import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { AuthLayout } from '../../layouts/AuthLayout';
import { Notice, Spinner } from '../../components/ui';
import { clearSocial, pendingSocial, signInWithProvider } from '../../lib/auth';
import { useSession } from '../../lib/session';

/**
 * Where Google and Apple send people back. Amplify swaps the code for tokens;
 * we then load the profile. If the account was just linked to an existing
 * EducAitor account, Cognito reports EDUCAITOR_LINKED once and we go round
 * again automatically, which lands in the linked account.
 */
export function CallbackPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const urlError = url.searchParams.get('error_description');

    const fail = (message: string) => {
      const pending = pendingSocial();
      if (/EDUCAITOR_LINKED/.test(message) && pending && !pending.retried) {
        signInWithProvider(pending.provider, { retry: true }).catch(() => setError('We couldn’t finish signing you in. Try again.'));
        return;
      }
      clearSocial();
      setError(
        /EDUCAITOR_LINKED/.test(message)
          ? 'Your Google or Apple account is now linked to your EducAitor account. Sign in again to continue.'
          : 'We couldn’t finish signing you in. Try again, or use your email and password.',
      );
    };

    if (urlError) {
      fail(urlError);
      return;
    }

    const stop = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        clearSocial();
        await refresh();
        navigate('/', { replace: true });
      } else if (payload.event === 'signInWithRedirect_failure') {
        const data = (payload as { data?: { error?: { message?: string } } }).data;
        fail(data?.error?.message ?? '');
      }
    });
    return stop;
  }, [refresh, navigate]);

  return (
    <AuthLayout title={error ? 'Sign-in didn’t finish' : 'Signing you in'}>
      {error ? (
        <>
          <Notice tone="error">{error}</Notice>
          <p className="auth-foot">
            <Link to="/sign-in">Back to sign in</Link>
          </p>
        </>
      ) : (
        <Spinner />
      )}
    </AuthLayout>
  );
}
